import { ref } from "vue";
import { apiDevicesUpdate } from "./api";
import { takeMediaDesired } from "./state";
import { tracksTask } from "./createTrackTask";
import { blurTask } from "./videoBlur";
import { noiseSuppressionTask } from "./audioProcessor";
import { muteTask } from "./muteTask";
import { livekitTask } from "./livekit";
import { log } from "../../utils/logger";

// Жизненный цикл медиа-движка:
// - init() (state.ts) ставит sessionActive=true в самом начале и вызывает
//   activatePipeline() в конце;
// - cleanup() (state.ts) вызывает stopSession() — синхронный «стоп-кран».
// После него ни один незавершённый async-колбэк не имеет права создавать
// треки/публикации или менять состояние. Каждая операция (проход пайплайна,
// init) захватывает поколение сессии в момент старта и перед «коммитом»
// (присвоение refs, setProcessor, publishTrack и т.п.) проверяет его через
// isCurrentSession: при мёртвой сессии — стопает созданное и выходит.
export const sessionActive = ref(false);

// Пайплайн может запускаться только после того, как init полностью выполнен
// (до этого желаемое состояние накапливается в dirty и разбирается на старте).
let pipeActive = false;

// Поколение сессии: растёт в stopSession(). Отличает продолжения старой сессии
// от новой (булев sessionActive после повторного init снова true — поколение
// при этом уже другое).
let sessionGeneration = 0;

export function currentSessionGeneration(): number {
  return sessionGeneration;
}

/** Коммит разрешён, только если операция принадлежит текущей сессии. */
export function isCurrentSession(generation: number): boolean {
  return sessionActive.value && generation === sessionGeneration;
}

const dirty = ref(false);
let working = false;

/**
 * Событие, меняющее желаемое состояние (действие пользователя, падение трека,
 * devicechange, ws-событие и т.д.): помечает движок грязным. Конвергенция
 * выполняется проходами pipeline — строго по одному; события, пришедшие во
 * время прохода, применяются следующим проходом.
 */
export function needSync() {
  dirty.value = true;
  if (pipeActive) void runPipeline();
}

/** init(): включить конвейер после того, как сессия инициализирована. */
export function activatePipeline() {
  pipeActive = true;
  needSync();
}

/** cleanup(): синхронно погасить сессию — новые треки/публикации невозможны. */
export function stopSession() {
  sessionActive.value = false;
  pipeActive = false;
  dirty.value = false;
  sessionGeneration++;
}

async function runPipeline() {
  if (working || !pipeActive) return;
  working = true;
  // Проходы принадлежат сессии, захваченной на старте executor'а. Если
  // cleanup → повторный init (новое поколение) случились, пока executor ждал
  // await, цикл старой сессии выходит и передаёт управление через finally,
  // который запускает runPipeline заново уже для новой сессии.
  const generation = currentSessionGeneration();
  try {
    log.debug("pipeline running");
    while (dirty.value && isCurrentSession(generation)) {
      dirty.value = false;
      // Желаемое состояние фиксируется на старте прохода: часть прохода не
      // может работать на одном состоянии, а часть — на новом. Мутации,
      // произошедшие во время прохода, применяются следующим проходом.
      const desired = takeMediaDesired();
      try {
        apiDevicesUpdate({
          camera: desired.cameraEnabled,
          micro: desired.microEnabled,
        });
        await tracksTask(desired);
        await noiseSuppressionTask(desired);
        await muteTask(desired);
        await blurTask(desired);
        await livekitTask(desired);
      } catch (e) {
        console.log("err pipeline", e);
        await delay(1000);
        if (!isCurrentSession(generation)) break;
        dirty.value = true;
      }
    }
  } finally {
    working = false;
    // needSync, прилетевший в момент выхода из цикла, не должен теряться.
    if (dirty.value && pipeActive) void runPipeline();
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
